import React, { useEffect, useMemo, useState } from 'react';
import DsButton from '../../DsButton';
import DsModal from '../../DsModal';

const DEFAULT_OPTIONS = ['修正なし', '修正あり'];

export type ChatQuestionComposerInput = {
  supplementalMessage: string;
  questionText: string;
  options: string[];
  responseDeadlineAt: string;
};

type Props = {
  open: boolean;
  initialSupplementalMessage?: string;
  submitting?: boolean;
  onClose: () => void;
  onSubmit?: (input: ChatQuestionComposerInput) => void | Promise<void>;
};

const toLocalInputValue = (date: Date): string => {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const defaultDeadline = () => {
  const date = new Date(Date.now() + (24 * 60 * 60 * 1000));
  date.setMinutes(Math.ceil(date.getMinutes() / 10) * 10, 0, 0);
  return toLocalInputValue(date);
};

export default function ChatQuestionComposerModal({
  open,
  initialSupplementalMessage = '',
  submitting = false,
  onClose,
  onSubmit,
}: Props) {
  const [supplementalMessage, setSupplementalMessage] = useState('');
  const [questionText, setQuestionText] = useState('');
  const [options, setOptions] = useState(DEFAULT_OPTIONS);
  const [responseDeadlineAt, setResponseDeadlineAt] = useState(defaultDeadline);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setSupplementalMessage(String(initialSupplementalMessage || ''));
    setQuestionText('');
    setOptions(DEFAULT_OPTIONS);
    setResponseDeadlineAt(defaultDeadline());
    setError('');
  }, [initialSupplementalMessage, open]);

  const canSubmit = useMemo(() => (
    questionText.trim().length > 0
    && options.length >= 2
    && options.length <= 5
    && options.every((option) => option.trim())
    && Boolean(responseDeadlineAt)
  ), [options, questionText, responseDeadlineAt]);

  const updateOption = (index: number, value: string) => {
    setOptions((current) => current.map((option, currentIndex) => (
      currentIndex === index ? value : option
    )));
  };

  const submit = async () => {
    if (!canSubmit || submitting) return;
    const deadline = new Date(responseDeadlineAt);
    if (!Number.isFinite(deadline.getTime()) || deadline.getTime() <= Date.now() + 120_000) {
      setError('回答期限は2分以上先に設定してください。');
      return;
    }
    if (new Set(options.map((option) => option.trim().toLocaleLowerCase('ja-JP'))).size !== options.length) {
      setError('同じ回答選択肢は登録できません。');
      return;
    }
    setError('');
    try {
      await onSubmit?.({
        supplementalMessage: supplementalMessage.trim(),
        questionText: questionText.trim(),
        options: options.map((option) => option.trim()),
        responseDeadlineAt: deadline.toISOString(),
      });
    } catch (submitError) {
      setError(
        submitError instanceof Error && submitError.message
          ? submitError.message
          : '質問メッセージを送信できませんでした。',
      );
    }
  };

  return (
    <DsModal
      open={open}
      onClose={onClose}
      title="質問メッセージを作成"
      ariaLabel="質問メッセージを作成"
      size="md"
      dismissible={!submitting}
      className="chat-question-composer"
      footer={(
        <>
          <DsButton variant="secondary" size="sm" onClick={onClose} disabled={submitting}>
            キャンセル
          </DsButton>
          <DsButton size="sm" onClick={submit} loading={submitting} disabled={!canSubmit}>
            質問メッセージを送信
          </DsButton>
        </>
      )}
    >
      <p className="chat-question-composer__intro">
        回答対象者へ通常チャットとは異なる「要回答」の通知を送ります。未回答の場合は期限前に1回リマインドします。
      </p>
      <label className="chat-question-composer__field">
        <span>通常のチャットメッセージ（任意）</span>
        <small className="form-help">
          質問カードの前に、通常のチャットメッセージとして表示されます。
        </small>
        <textarea
          className="textarea"
          value={supplementalMessage}
          onChange={(event) => setSupplementalMessage(event.target.value)}
          maxLength={5000}
          rows={3}
          placeholder="確認してほしい背景や対象を入力"
        />
      </label>
      <label className="chat-question-composer__field">
        <span>質問 <strong>必須</strong></span>
        <textarea
          className="textarea"
          value={questionText}
          onChange={(event) => setQuestionText(event.target.value)}
          maxLength={1000}
          rows={3}
          placeholder="例：この紹介文で公開して問題ありませんか？"
        />
      </label>
      <fieldset className="chat-question-composer__options">
        <legend>回答選択肢（2〜5件）</legend>
        {options.map((option, index) => (
          <div className="chat-question-composer__option" key={index}>
            <span>{index + 1}</span>
            <input
              type="text"
              className="input"
              value={option}
              onChange={(event) => updateOption(index, event.target.value)}
              maxLength={100}
              aria-label={`回答選択肢 ${index + 1}`}
            />
            {options.length > 2 ? (
              <DsButton
                icon
                variant="ghost"
                size="sm"
                aria-label={`回答選択肢 ${index + 1} を削除`}
                onClick={() => setOptions((current) => current.filter((_, currentIndex) => currentIndex !== index))}
              >
                <i className="fa-solid fa-xmark" aria-hidden="true" />
              </DsButton>
            ) : null}
          </div>
        ))}
        {options.length < 5 ? (
          <DsButton
            variant="create"
            size="sm"
            onClick={() => setOptions((current) => [...current, ''])}
          >
            選択肢を追加
          </DsButton>
        ) : null}
      </fieldset>
      <label className="chat-question-composer__field">
        <span>回答期限 <strong>必須</strong></span>
        <input
          type="datetime-local"
          className="input"
          value={responseDeadlineAt}
          min={toLocalInputValue(new Date(Date.now() + 120_000))}
          onChange={(event) => setResponseDeadlineAt(event.target.value)}
        />
      </label>
      {error ? <div className="chat-question-composer__error" role="alert">{error}</div> : null}
    </DsModal>
  );
}
